/**
 * LES IMAGES DE L'ENVIRONNEMENT : DU FICHIER CHOISI A LA DATA URL ENREGISTRABLE.
 *
 * DEUX IMAGES, DEUX ROLES, UN SEUL CHEMIN DE CODE :
 *   - le LOGO, le lockup large de l'ecran de connexion et de l'accueil ;
 *   - l'ICONE, la marque carree au-dessus du dock et dans l'onglet du
 *     navigateur.
 * Elles ne different que par leur GABARIT (la taille visee et la borne des
 * rules), pas par leur traitement : meme liste de formats, meme canvas, meme
 * refus. Un deuxieme pipeline copie-colle aurait diverge au premier correctif.
 *
 * POURQUOI DU CLIENT ET PAS DU STORAGE : ces images vivent dans le document
 * `orgs/{orgId}/settings/branding`, lu PUBLIQUEMENT (le login affiche le nom et
 * le logo avant toute connexion). Un seul document, une seule lecture, aucun
 * bucket a ouvrir ni a securiser. Le prix de ce choix est une BORNE DURE : les
 * rules refusent une data URL trop longue, et Firestore refuse un document de
 * plus d'un Mo. L'ecran ne peut donc pas se contenter de lire le fichier : il le
 * RAMENE sous la borne, ou il refuse en le disant.
 *
 * DEUX CHEMINS, PARCE QUE DEUX NATURES :
 *   - un SVG est du texte : on ne le redimensionne pas (il est deja sans
 *     echelle), on l'encode tel quel et on refuse s'il est trop lourd ;
 *   - un PNG, un JPEG ou un WebP est une image matricielle : on la redessine
 *     sur un canvas au plus grand cote vise, et on ressort en PNG, jamais en
 *     JPEG, parce qu'un logo a presque toujours un fond TRANSPARENT que le JPEG
 *     remplacerait par du noir.
 *
 * CE QUI EST PUR VIT ICI AUSSI, ET SE PROUVE (`tests/environnement.test.mjs`) :
 * la liste des types, les bornes, l'echelle des tentatives et le calcul de la
 * taille cible. Seule `dataUrlDeFichierImage` touche le DOM (canvas, `File`), et
 * elle ne decide rien : elle applique ce que les fonctions pures disent.
 */

/**
 * Les types d'image acceptes, exactement ceux que les Firestore Security Rules
 * laissent passer. Cette liste ecrite ailleurs finirait par diverger des rules :
 * elle sert ICI a refuser tot, et l'attribut `accept` du champ fichier en est
 * derive, jamais recopie.
 */
export const TYPES_LOGO = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
] as const;

/** L'attribut `accept` du champ fichier, derive de la liste ci-dessus. */
export const ACCEPT_LOGO = TYPES_LOGO.join(',');

/**
 * La borne des rules, en CARACTERES de la data URL (pas en octets du fichier) :
 * c'est la chaine qui part dans le document, donc c'est elle qui se mesure.
 */
export const LOGO_MAX_CARACTERES = 300000;

/**
 * Les plus grands cotes tentes, du plus genereux au plus serre. On redescend
 * tant que le PNG produit depasse la borne ; epuisee, la liste fait REFUSER
 * avec une phrase claire, jamais enregistrer une image tronquee.
 *
 * 640 est genereux pour un logo affiche en haut de l'accueil (sa hauteur vit
 * dans `logoAccueil.tsx`) : il couvre un ecran a 3x sans rien coder de plus.
 */
export const TAILLES_TENTEES = [640, 400, 256] as const;

/**
 * La borne de l'ICONE, plus serree : elle s'affiche a 20 px au-dessus du dock et
 * a 32 px dans l'onglet du navigateur. La MEME valeur est ecrite dans les rules
 * et dans `packages/sdk/src/branding.ts`.
 */
export const ICONE_MAX_CARACTERES = 100000;

/**
 * Les cotes tentes pour une icone. 128 couvre un onglet a 3x et le dock a 4x ;
 * au-dela on ne paie que du poids dans un document lu par tous les visiteurs.
 */
export const TAILLES_ICONE = [128, 96, 64] as const;

/**
 * CE QUI DISTINGUE UNE IMAGE D'UNE AUTRE : ou l'on vise, jusqu'ou l'on va, et ce
 * qu'on dit quand ca ne rentre pas. Tout le reste est commun.
 */
export interface GabaritImage {
  /** Les plus grands cotes tentes, du plus genereux au plus serre. */
  tailles: readonly number[];
  /** La borne des rules, en caracteres de la data URL. */
  maxCaracteres: number;
  /** La phrase du refus quand les tentatives sont epuisees. */
  refusTropLourd: string;
}

/** Le type MIME du fichier est-il un de ceux qu'on accepte ? */
export function typeAccepte(type: string): boolean {
  return (TYPES_LOGO as readonly string[]).includes(type);
}

/** Le fichier est-il un SVG ? Le seul type qui ne passe pas par le canvas. */
export function estSvg(type: string): boolean {
  return type === 'image/svg+xml';
}

/**
 * La taille de dessin, proportions gardees, quand on vise `max` sur le plus
 * grand cote. Une image DEJA plus petite n'est pas agrandie : l'agrandir
 * alourdirait le PNG sans ajouter un seul pixel d'information.
 */
export function tailleCible(
  largeur: number,
  hauteur: number,
  max: number,
): { largeur: number; hauteur: number } {
  const plusGrand = Math.max(largeur, hauteur);
  if (plusGrand <= 0 || max <= 0) return { largeur: 0, hauteur: 0 };
  if (plusGrand <= max) return { largeur, hauteur };
  const echelle = max / plusGrand;
  /* Au moins un pixel par cote : un logo tres allonge (une bande de 2000 x 20)
     arrondirait sinon sa hauteur a zero, et le canvas rendrait une image vide. */
  return {
    largeur: Math.max(1, Math.round(largeur * echelle)),
    hauteur: Math.max(1, Math.round(hauteur * echelle)),
  };
}

/**
 * `data:image/svg+xml;base64,...` depuis le TEXTE du fichier.
 *
 * BASE64 ET PAS UN SVG EN CLAIR : une data URL non encodee devrait echapper le
 * `#` des couleurs et les guillemets, et le moindre oubli casserait l'image
 * chez le client sans rien dire ici. L'encodage passe par l'UTF-8 octet par
 * octet parce que `btoa` ne prend que du latin1, et qu'un SVG porte volontiers
 * un accent ou un glyphe dans un `<title>`.
 */
export function dataUrlDeSvg(texte: string): string {
  const octets = new TextEncoder().encode(texte);
  let binaire = '';
  for (const octet of octets) binaire += String.fromCharCode(octet);
  return `data:image/svg+xml;base64,${btoa(binaire)}`;
}

/** La phrase du refus quand un fichier reste trop lourd, tentatives epuisees. */
export const REFUS_TROP_LOURD =
  'Ce logo reste trop lourd une fois reduit. Prends un fichier plus simple (un SVG, ou un PNG avec moins de details).';

/** La meme, pour l'icone : elle nomme l'image dont on parle, pas « le fichier ». */
export const REFUS_ICONE_TROP_LOURDE =
  'Cette icone reste trop lourde une fois reduite. Prends un fichier plus simple (un SVG, ou un PNG avec moins de details).';

/** Le gabarit du LOGO : le lockup large de la connexion et de l'accueil. */
export const GABARIT_LOGO: GabaritImage = {
  tailles: TAILLES_TENTEES,
  maxCaracteres: LOGO_MAX_CARACTERES,
  refusTropLourd: REFUS_TROP_LOURD,
};

/** Le gabarit de l'ICONE : la marque carree du dock et de l'onglet. */
export const GABARIT_ICONE: GabaritImage = {
  tailles: TAILLES_ICONE,
  maxCaracteres: ICONE_MAX_CARACTERES,
  refusTropLourd: REFUS_ICONE_TROP_LOURDE,
};

/** La phrase du refus quand le fichier n'est pas une image acceptee. */
export const REFUS_TYPE =
  'Format refuse. Choisis un fichier PNG, JPEG, WebP ou SVG.';

/**
 * L'image decodee et ses dimensions, plus de quoi la liberer. Deux chemins :
 * `createImageBitmap` quand le navigateur le propose (il decode hors du thread
 * principal), un `Image` sur une URL d'objet sinon.
 */
async function imageDe(
  fichier: File,
): Promise<{ source: CanvasImageSource; largeur: number; hauteur: number; liberer: () => void }> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(fichier);
    return {
      source: bitmap,
      largeur: bitmap.width,
      hauteur: bitmap.height,
      liberer: () => bitmap.close(),
    };
  }
  const url = URL.createObjectURL(fichier);
  try {
    const image = await new Promise<HTMLImageElement>((resoudre, rejeter) => {
      const element = new Image();
      element.onload = () => resoudre(element);
      element.onerror = () => rejeter(new Error("Ce fichier n'a pas pu etre lu comme une image."));
      element.src = url;
    });
    return {
      source: image,
      largeur: image.naturalWidth,
      hauteur: image.naturalHeight,
      liberer: () => URL.revokeObjectURL(url),
    };
  } catch (cause) {
    URL.revokeObjectURL(url);
    throw cause;
  }
}

/**
 * Le fichier choisi, ramene a une data URL enregistrable selon son GABARIT
 * (logo ou icone), ou une erreur dont le message s'affiche tel quel a l'ecran.
 *
 * C'est la SEULE fonction de ce module qui touche le DOM : tout ce qu'elle
 * decide est decide au-dessus, par les fonctions pures.
 */
export async function dataUrlDeFichierImage(
  fichier: File,
  gabarit: GabaritImage,
): Promise<string> {
  if (!typeAccepte(fichier.type)) throw new Error(REFUS_TYPE);

  if (estSvg(fichier.type)) {
    const dataUrl = dataUrlDeSvg(await fichier.text());
    if (dataUrl.length > gabarit.maxCaracteres) throw new Error(gabarit.refusTropLourd);
    return dataUrl;
  }

  const { source, largeur, hauteur, liberer } = await imageDe(fichier);
  try {
    for (const max of gabarit.tailles) {
      const cible = tailleCible(largeur, hauteur, max);
      const canvas = document.createElement('canvas');
      canvas.width = cible.largeur;
      canvas.height = cible.hauteur;
      const contexte = canvas.getContext('2d');
      if (contexte === null) {
        throw new Error("Ce navigateur ne sait pas preparer l'image. Choisis un fichier SVG.");
      }
      /* Aucun fond n'est pose : le canvas nait transparent et le PNG garde
         cette transparence. Peindre du blanc « pour faire propre » collerait un
         rectangle blanc derriere un logo pose sur fond sombre. */
      contexte.drawImage(source, 0, 0, cible.largeur, cible.hauteur);
      const dataUrl = canvas.toDataURL('image/png');
      if (dataUrl.length <= gabarit.maxCaracteres) return dataUrl;
    }
    throw new Error(gabarit.refusTropLourd);
  } finally {
    liberer();
  }
}
